import { useCallback, useEffect, useMemo } from "react";
import { Calendar, Building2, MapPin, Landmark, Users } from "lucide-react";
import { TextInput, SelectInput, MaskedInput, NumberInput } from "../components/fields";
import { useFormStore } from "../store/formStore";
import { useZipLookup } from "../hooks/useZipLookup";
import { Card } from "../components/Card";
import { FieldRow } from "../components/FieldRow";
import type { useStepValidation } from "../hooks/useStepValidation";

interface StepCompanyProps {
  validation: ReturnType<typeof useStepValidation>;
}

const ZIP_DEBOUNCE_MS = 750;

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function buildEffectiveDateOptions(): Array<{ value: string; label: string }> {
  const now = new Date();
  const options: Array<{ value: string; label: string }> = [{ value: "", label: "Select..." }];
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const value = `${yyyy}-${mm}-01`;
    const label = `${MONTH_NAMES[d.getMonth()]} 1, ${yyyy}`;
    options.push({ value, label });
  }
  return options;
}

const effectiveDateOptions = buildEffectiveDateOptions();

export function StepCompany({ validation }: StepCompanyProps) {
  const zipLookup = useZipLookup();
  const zipValue = useFormStore((s) => s.data.zipCode);
  const cityValue = useFormStore((s) => s.data.city);
  const orgTypeOptions = useFormStore((s) => s.meta.organizationTypeOptions);
  const currentOrgType = useFormStore((s) => s.data.orgType);
  const sicCodes = useFormStore((s) => s.meta.sicCodes);
  const sicValue = useFormStore((s) => s.data.sicCode);

  // Fire the lookup ~750ms after the user stops editing the zip, not on blur,
  // so the spinner + city/state visual blank kick in immediately while the
  // user is still mid-flow. The debounce avoids re-fetches when someone types
  // a typo'd 5th digit and then backspaces. We run the lookup for every zip
  // value — not just complete 5-digit ones — so that clearing or shortening a
  // resolved zip clears the dependent city/state (the hook no-ops on
  // incomplete zips that never resolved). The hook's `lastZipRef`
  // short-circuits duplicate lookups, so this is a no-op on mount when the
  // prefilled zip matches the seeded ref. Depend on `lookup` (useCallback-
  // stable) rather than the wrapper object, otherwise every keystroke
  // anywhere on the form re-runs this effect and clears the pending timer.
  const zipLookupCb = zipLookup.lookup;
  useEffect(() => {
    const timer = setTimeout(() => zipLookupCb(zipValue), ZIP_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [zipValue, zipLookupCb]);

  // When a zip lookup returned multiple city+state pairs, the city field
  // becomes a dropdown. Deduplicate by city name for the dropdown; the
  // state is driven separately. If the same city appears in 2 states, the
  // user picks the city and then picks the state from the state dropdown.
  const setField = useFormStore((s) => s.setField);
  const citySelectOptions = useMemo(() => {
    const uniqueCities = Array.from(new Set(zipLookup.cityOptions.map((c) => c.city)));
    if (uniqueCities.length < 2) return null;
    // Ensure the currently-selected city is always in the option list.
    if (cityValue && !uniqueCities.includes(cityValue)) uniqueCities.unshift(cityValue);
    return uniqueCities;
  }, [zipLookup.cityOptions, cityValue]);

  // State is only a dropdown when the selected city spans multiple states
  // (e.g. EMERYVILLE in both CA and NV). Otherwise it's readonly.
  const stateValue = useFormStore((s) => s.data.state);
  const stateOptions = useMemo(() => {
    if (!cityValue) return null;
    const statesForCity = Array.from(new Set(
      zipLookup.cityOptions
        .filter((c) => c.city === cityValue)
        .map((c) => c.stateName)
        .filter(Boolean)
    ));
    if (statesForCity.length < 2) return null;
    if (stateValue && !statesForCity.includes(stateValue)) statesForCity.unshift(stateValue);
    return statesForCity;
  }, [zipLookup.cityOptions, cityValue, stateValue]);

  // When city changes via dropdown, auto-update state if the city maps to
  // exactly one state. If it spans multiple states, leave the state dropdown
  // for the user to pick.
  const handleCityChange = useCallback((city: string) => {
    const matchingStates = zipLookup.cityOptions
      .filter((c) => c.city === city)
      .map((c) => c.stateName);
    const unique = Array.from(new Set(matchingStates));
    if (unique.length === 1) setField("state", unique[0]);
  }, [zipLookup.cityOptions, setField]);

  // Append the saved value if SF removed it from the picklist after it was
  // stored, so the user still sees their current selection rather than a
  // silently-blank field.
  const orgTypeSelectOptions = useMemo(() => {
    const options = ["", ...orgTypeOptions];
    if (currentOrgType && !options.includes(currentOrgType)) {
      options.push(currentOrgType);
    }
    return options;
  }, [orgTypeOptions, currentOrgType]);

  // Look up the industry name from the cached SIC codes list and auto-set
  // the sicDescription field so it persists to SF.
  const sicMatch = useMemo(
    () => sicCodes.find((s) => s.code === sicValue) ?? null,
    [sicCodes, sicValue],
  );
  useEffect(() => {
    if (sicCodes.length === 0) return;
    const desc = sicMatch?.industryName ?? "";
    if (desc !== useFormStore.getState().data.sicDescription) {
      setField("sicDescription", desc);
    }
  }, [sicMatch, sicCodes, setField]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-brand-navy mb-1">Company Information</h1>
        <p className="text-gray-500">Tell us about your business</p>
      </div>

      <Card title="Effective Date" icon={Calendar}>
        <SelectInput
          field="effectiveDate"
          label="Coverage Effective Date"
          validation={validation}
          options={effectiveDateOptions}
          placeholder="Select effective date..."
          hint="Coverage begins on the 1st of the selected month."
          required
        />
      </Card>

      <Card title="Business Details" icon={Building2}>
        <div className="mb-4">
          <TextInput
            field="companyLegalName"
            label="Company Legal Name"
            validation={validation}
            placeholder="Enter legal company name"
            hint="This name will appear on all downstream documents and must match across the entire submission package."
            maxLength={35}
            required
          />
        </div>
        <div className="mb-4">
          <TextInput
            field="dba"
            label="DBA (Doing Business As)"
            validation={validation}
            placeholder="If different from legal name"
            maxLength={255}
          />
        </div>
        <MaskedInput
          field="federalTaxId"
          label="Employer Federal Tax ID"
          mask="ein"
          validation={validation}
          required
          noPrefill
        />
      </Card>

      <Card title="Physical Address" icon={MapPin}>
        <div className="mb-4">
          <TextInput
            field="streetAddress"
            label="Street Address"
            validation={validation}
            placeholder="123 Main Street"
            maxLength={255}
            required
          />
        </div>
        <div className="mb-4">
          <MaskedInput
            field="zipCode"
            label="Zip Code"
            mask="zip"
            validation={validation}
            hint="City and State will auto-populate from Zip Code"
            required
          />
          {zipLookup.error && (
            <p role="alert" className="text-xs mt-1 text-danger">{zipLookup.error}</p>
          )}
        </div>
        <FieldRow>
          {citySelectOptions && !zipLookup.loading ? (
            <SelectInput
              field="city"
              label="City"
              validation={validation}
              options={citySelectOptions}
              hint="Auto-populated from Zip Code"
              onChange={handleCityChange}
            />
          ) : (
            <TextInput
              field="city"
              label="City"
              validation={validation}
              readOnly
              loading={zipLookup.loading}
              hint="Auto-populated from Zip Code"
            />
          )}
          {stateOptions && !zipLookup.loading ? (
            <SelectInput
              field="state"
              label="State"
              validation={validation}
              options={stateOptions}
              hint="Auto-populated from Zip Code"
            />
          ) : (
            <TextInput
              field="state"
              label="State"
              validation={validation}
              readOnly
              loading={zipLookup.loading}
              hint="Auto-populated from Zip Code"
            />
          )}
        </FieldRow>
      </Card>

      <Card title="Organization Details" icon={Landmark}>
        <div className="mb-4">
          <SelectInput
            field="orgType"
            label="Organization Type"
            validation={validation}
            options={orgTypeSelectOptions}
            placeholder="Select organization type..."
            required
          />
        </div>
        <div className="mb-4">
          <div className="flex gap-3">
            <label htmlFor="sicCode" className="w-1/4 shrink-0 text-sm font-medium text-gray-700">SIC Code<span className="text-danger" aria-hidden="true">&nbsp;*</span></label>
            <span className="text-sm font-medium text-gray-700">Industry</span>
          </div>
          <div className="flex gap-3 mt-1">
            <MaskedInput
              field="sicCode"
              label=""
              mask="sic"
              validation={validation}
              required
              containerClassName="w-1/4 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <output
                aria-label="Industry"
                title={sicMatch?.industryName ?? ""}
                className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 truncate"
              >
                {sicMatch?.industryName || (
                  sicValue.length >= 3 && sicCodes.length > 0 && !sicMatch
                    ? <span className="text-gray-400">Enter a valid SIC</span>
                    : <span className="text-gray-400">&nbsp;</span>
                )}
              </output>
            </div>
          </div>
        </div>
        <MaskedInput
          field="yearEstablished"
          label="Year Business Established"
          mask="year"
          validation={validation}
          required
        />
      </Card>

      <Card title="Group Size" icon={Users}>
        <div className="mb-4">
          <NumberInput
            field="avgW2Employees"
            label="During typical business days in the last full Calendar Year, how many W2 employees (on average) worked for your firm?"
            validation={validation}
            hint="You should include full- and/or part-time employees, regardless of hours or how long you employed them."
            min={1}
            maxLength={4}
            required
            placeholder="e.g. 25"
          />
        </div>
        <div>
          <NumberInput
            field="avgW2Employees20Weeks"
            label="How many average full- and/or part-time W2 employees did you employ for every working day in each of 20 or more calendar weeks in the last full Calendar Year?"
            validation={validation}
            hint="The 20 weeks do not have to be consecutive."
            min={1}
            maxLength={4}
            required
            placeholder="e.g. 22"
          />
        </div>
      </Card>
    </div>
  );
}
